import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Mail, FileText } from 'lucide-react';

export const EmailTemplates = () => {
  const { get, post, put, del } = useApi();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    kategorie: ''
  });

  const loadTemplates = useCallback(async () => {
    try {
      const data = await get('/email-templates');
      setTemplates(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleOpenCreate = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      subject: '',
      body: '',
      kategorie: ''
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      kategorie: template.kategorie || ''
    });
    setDialogOpen(true);
  };

  const handleOpenDelete = (template) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedTemplate) {
        await put(`/email-templates/${selectedTemplate.id}`, formData);
        toast.success('Vorlage aktualisiert');
      } else {
        await post('/email-templates', formData);
        toast.success('Vorlage erstellt');
      }
      setDialogOpen(false);
      loadTemplates();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async () => {
    try {
      await del(`/email-templates/${selectedTemplate.id}`);
      toast.success('Vorlage gelöscht');
      setDeleteDialogOpen(false);
      loadTemplates();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="email-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">E-Mail-Vorlagen</h1>
          <p className="text-gray-500 mt-1">Verwalten Sie Ihre E-Mail-Vorlagen für den Vertrieb</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#E63946] hover:bg-[#D62839]"
              onClick={handleOpenCreate}
              data-testid="create-template-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Neue Vorlage
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {selectedTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
              </DialogTitle>
              <DialogDescription>
                Erstellen Sie eine E-Mail-Vorlage für wiederkehrende Nachrichten
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="template-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name der Vorlage *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="z.B. Erstansprache"
                    required
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kategorie</Label>
                  <Input
                    value={formData.kategorie}
                    onChange={(e) => setFormData({ ...formData, kategorie: e.target.value })}
                    placeholder="z.B. Akquise, Follow-up..."
                    data-testid="template-category-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Betreff *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="E-Mail-Betreff"
                  required
                  data-testid="template-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Inhalt *</Label>
                <textarea
                  className="w-full min-h-[200px] p-3 border border-gray-200 rounded-md focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] outline-none font-mono text-sm"
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Sehr geehrte/r {{ansprechpartner}},...&#10;&#10;Verwenden Sie {{firma}}, {{ansprechpartner}}, {{rolle}} als Platzhalter."
                  required
                  data-testid="template-body-input"
                />
                <p className="text-xs text-gray-500">
                  Platzhalter: {"{{firma}}"}, {"{{ansprechpartner}}"}, {"{{rolle}}"}, {"{{email}}"}
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-[#E63946] hover:bg-[#D62839]" data-testid="template-submit-button">
                  {selectedTemplate ? 'Aktualisieren' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Mail className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Keine Vorlagen vorhanden</h3>
            <p className="text-gray-500 text-center mb-4">
              Erstellen Sie Ihre erste E-Mail-Vorlage, um schneller E-Mails zu versenden
            </p>
            <Button 
              className="bg-[#E63946] hover:bg-[#D62839]"
              onClick={handleOpenCreate}
            >
              <Plus className="h-4 w-4 mr-2" />
              Erste Vorlage erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${template.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#E63946]" />
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEdit(template)}
                      data-testid={`edit-template-${template.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDelete(template)}
                      data-testid={`delete-template-${template.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                {template.kategorie && (
                  <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded mt-1">
                    {template.kategorie}
                  </span>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-500">Betreff</p>
                    <p className="font-medium text-sm truncate">{template.subject}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Vorschau</p>
                    <p className="text-sm text-gray-700 line-clamp-3">{template.body}</p>
                  </div>
                  <div className="pt-2 border-t text-xs text-gray-400">
                    Aktualisiert: {formatDate(template.updated_at)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vorlage löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die Vorlage "{selectedTemplate?.name}" wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-template"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
